import axios from "axios";

const SKIP_SESSION_RESTORE_STORAGE_KEY = "nibhv_skip_session_restore_once";
const SKIP_NEXT_AUTH_RECHECK_STORAGE_KEY = "nibhv_skip_next_auth_recheck_once";
const AUTH_ENDPOINTS = ["/v2/auth/login/"];

let authFailureHandler = null;
let interceptorsInstalled = false;
let authFailureHandled = false;
let skipNextAuthRecheckInMemory = false;

function canUseWindow() {
  return typeof window !== "undefined";
}

function getSessionStorage() {
  if (!canUseWindow()) {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
}

function getUrlPath(url) {
  if (!url) {
    return "";
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return new URL(url).pathname;
  }
  return url.split("?", 1)[0];
}

function isAuthEndpoint(url) {
  const path = getUrlPath(url);
  return AUTH_ENDPOINTS.some((endpoint) => path === endpoint || path === endpoint + "/");
}

function shouldHandleAuthFailure(config) {
  if (config && (config.skipAuthRefresh || config.skipAuthFailure)) {
    return false;
  }

  return !isAuthEndpoint(config && config.url);
}

function shouldTriggerAuthFailureForResponse(response) {
  if (!response) {
    return false;
  }

  if (response.status === 401) {
    return true;
  }

  return response.status === 403 && response.data && response.data.code === "invalid_session";
}

function handleAuthFailure(error) {
  if (authFailureHandled) {
    return;
  }

  authFailureHandled = true;
  if (typeof authFailureHandler === "function") {
    authFailureHandler(error);
  }
}

export function setAuthFailureHandler(handler) {
  authFailureHandler = handler;
}

export function clearAuthSession() {
  authFailureHandled = false;
}

export function markSkipNextSessionRestore() {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(SKIP_SESSION_RESTORE_STORAGE_KEY, "1");
  } catch (error) {
    // Ignore storage issues to avoid blocking logout.
  }
}

export function consumeSkipNextSessionRestore() {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }
  try {
    const shouldSkip = storage.getItem(SKIP_SESSION_RESTORE_STORAGE_KEY) === "1";
    if (shouldSkip) {
      storage.removeItem(SKIP_SESSION_RESTORE_STORAGE_KEY);
    }
    return shouldSkip;
  } catch (error) {
    return false;
  }
}

export function markSkipNextAuthRecheck() {
  skipNextAuthRecheckInMemory = true;

  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(SKIP_NEXT_AUTH_RECHECK_STORAGE_KEY, "1");
  } catch (error) {
    // Ignore storage issues to avoid blocking login flow.
  }
}

export function consumeSkipNextAuthRecheck() {
  if (skipNextAuthRecheckInMemory) {
    skipNextAuthRecheckInMemory = false;
    return true;
  }

  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }
  try {
    const shouldSkip = storage.getItem(SKIP_NEXT_AUTH_RECHECK_STORAGE_KEY) === "1";
    if (shouldSkip) {
      storage.removeItem(SKIP_NEXT_AUTH_RECHECK_STORAGE_KEY);
    }
    return shouldSkip;
  } catch (error) {
    return false;
  }
}

export function setupAuthSessionInterceptors() {
  if (interceptorsInstalled) {
    return;
  }
  interceptorsInstalled = true;

  axios.interceptors.request.use((config) => config);

  axios.interceptors.response.use(
    (response) => {
      if (shouldTriggerAuthFailureForResponse(response) && shouldHandleAuthFailure(response && response.config)) {
        handleAuthFailure({ response: response, config: response && response.config });
        return response;
      }

      if (response && response.status < 400) {
        authFailureHandled = false;
      }
      return response;
    },
    async (error) => {
      const response = error && error.response;
      if (shouldTriggerAuthFailureForResponse(response) && shouldHandleAuthFailure((error && error.config) || (response && response.config))) {
        handleAuthFailure(error);
      }

      throw error;
    }
  );
}



// WEBPACK FOOTER //
// ./utils/authSession.js